import { z } from 'zod';
import { GraphSourceTool } from './source-tool.js';
/**
 * A per-(person × tool) identity record. Each Person node aggregates 1..N
 * of these — Trita in Slack is one ToolIdentity, Trita in Salesforce is
 * another. The probabilistic matcher operates over ToolIdentity pairs
 * across sources to decide whether they belong to the same Person.
 *
 * `source` + `externalId` is unique per org — the Cypher uniqueness
 * constraint enforces it. `email` / `handle` / `displayName` / `avatarUrl`
 * are the four scoring columns; any can be absent (different tools
 * expose different signal).
 */
export const ToolIdentity = z.object({
    source: GraphSourceTool,
    /** The source tool's own id for this identity (Slack uid, GH login, Salesforce contactId). */
    externalId: z.string().min(1),
    email: z.string().email().optional(),
    /** `@trita`, `tparsi`, or whatever handle the tool exposes. */
    handle: z.string().optional(),
    displayName: z.string().optional(),
    avatarUrl: z.string().url().optional(),
});
/**
 * The resolved Person — a single human across all connected tools. The
 * Person node carries an aggregated view (preferred displayName, the
 * union of emails/handles/avatars), and 1..N `ToolIdentity` entries
 * either embedded here or linked via `IDENTITY_OF` edges in the graph.
 * The embedded shape is the canonical wire format; the graph mirrors
 * it as nodes for path-traversal queries.
 *
 * Multi-tenant: `orgId` is mandatory and matches the Cypher constraint
 * `(orgId, externalId)` where `externalId = "person:" + canonicalKey`.
 */
export const Person = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** Preferred display name (usually picked from highest-signal tool). */
    displayName: z.string().min(1),
    /** Aggregate identities across all tools. */
    identities: z.array(ToolIdentity).min(1),
    /** Optional aggregate fields lifted to the top for convenience. */
    primaryEmail: z.string().email().optional(),
    primaryAvatarUrl: z.string().url().optional(),
    /** Computed: union of email domains → resolves WORKS_AT employer. */
    emailDomains: z.array(z.string().min(1)).default([]),
    createdAt: z.string(),
    updatedAt: z.string(),
});
